import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plane, ShieldCheck, Clock, MapPin, Car, ArrowRight, Check } from "lucide-react";
import heroImg from "@/assets/hero.webp";

const TITLE = "Miami Airport Luxury Car Rental | MIA Delivery | Creed";
const DESC = "Luxury car rental at Miami International Airport (MIA). Exotic, SUV & convertible curbside delivery at MIA & FLL. Insurance included, no hidden fees.";
const URL = "https://creedcarental.com/miami-airport-luxury-car-rental";

export const Route = createFileRoute("/miami-airport-luxury-car-rental")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://creedcarental.com${heroImg}` },
      { property: "og:image:width", content: "1920" },
      { property: "og:image:height", content: "1080" },
      { property: "og:image:alt", content: "Luxury car rental at Miami International Airport" },
      { name: "twitter:image", content: `https://creedcarental.com${heroImg}` },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Miami Airport Luxury Car Rental",
          serviceType: "Luxury car rental with airport delivery",
          areaServed: [
            { "@type": "Airport", name: "Miami International Airport", iataCode: "MIA" },
            { "@type": "Airport", name: "Fort Lauderdale-Hollywood International Airport", iataCode: "FLL" },
          ],
          provider: {
            "@type": "Organization",
            name: "Creed Car Rental",
            url: "https://creedcarental.com",
            areaServed: "Miami, FL",
          },
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Do you deliver luxury cars to Miami International Airport (MIA)?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. We deliver any car in our fleet curbside at MIA arrivals, typically within an hour of your flight landing. There is no airport surcharge.",
              },
            },
            {
              "@type": "Question",
              name: "Can you deliver to Fort Lauderdale Airport (FLL)?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes — we deliver to FLL as well as MIA, plus Miami hotels, South Beach, and Airbnb addresses across Miami-Dade.",
              },
            },
            {
              "@type": "Question",
              name: "Is insurance included in the rental?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Every Creed rental includes insurance — no upsells, no fine print.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: AirportLanding,
});

const features = [
  { Icon: Plane, title: "Curbside MIA delivery", body: "Meet your car at Miami International arrivals. Just send your flight number." },
  { Icon: Clock, title: "Replies within the hour", body: "Owner-run service. Real humans, not a call center — usually back to you in minutes." },
  { Icon: ShieldCheck, title: "Insurance included", body: "Every rental comes with insurance built in. No upsells at the curb." },
  { Icon: MapPin, title: "FLL & door-to-door", body: "Also delivering to Fort Lauderdale Airport, Miami hotels, South Beach, and Airbnbs." },
  { Icon: Car, title: "Hand-picked fleet", body: "Exotics, SUVs, and convertibles — each one chosen, maintained, and detailed in-house." },
  { Icon: Check, title: "No hidden fees", body: "No airport surcharge, no junk fees. The price you see is the price you pay." },
];

function AirportLanding() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-20 md:py-28">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">
            MIA · FLL · South Beach
          </div>
          <h1 className="mt-4 font-display text-4xl text-cream md:text-6xl">
            Miami Airport Luxury Car Rental
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-cream/75 md:text-lg">
            Skip the rental counter. Hand-picked exotics, SUVs and convertibles delivered curbside at Miami International Airport — insurance included, no hidden fees.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-accent text-espresso hover:bg-accent/90">
              <Link to="/fleet">
                Browse the fleet <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-cream/30 text-cream hover:bg-cream/10">
              <Link to="/contact">Get a quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="text-center">
          <div className="text-sm uppercase tracking-[0.25em] text-accent">Why Creed at MIA</div>
          <h2 className="mt-2 font-display text-3xl text-espresso md:text-4xl">
            The easiest way to land in a luxury car
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <Card key={f.title} className="rounded-tr-3xl border-border/60 bg-card p-6 shadow-sm">
              <f.Icon className="h-6 w-6 text-accent" />
              <div className="mt-4 font-display text-xl text-espresso">{f.title}</div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-accent">From flight to keys</div>
            <h2 className="mt-2 font-display text-3xl text-espresso md:text-4xl">
              How airport delivery works
            </h2>
          </div>
          <ol className="mt-10 space-y-5">
            {[
              { n: "1", t: "Pick your car", d: "Browse the fleet and tell us your dates plus flight number." },
              { n: "2", t: "We confirm", d: "Within the hour we'll confirm the car, price, and curbside meeting point at MIA." },
              { n: "3", t: "We track your flight", d: "If you're delayed, we adjust — no waiting fees, no calls to make." },
              { n: "4", t: "Drive away", d: "Meet your car at arrivals, sign the agreement, and you're off." },
            ].map((s) => (
              <li key={s.n} className="flex gap-4 rounded-tr-3xl bg-card p-5 shadow-sm">
                <span aria-hidden className="font-display text-3xl text-accent/80">{s.n}</span>
                <div>
                  <div className="font-display text-lg text-espresso">{s.t}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 py-20 text-center">
        <h2 className="font-display text-3xl text-espresso md:text-4xl">
          Landing soon? Let's get your car ready.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Send us your flight and dates — we'll have the right car waiting curbside at MIA.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="bg-accent text-espresso hover:bg-accent/90">
            <Link to="/fleet">Browse the fleet</Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/contact">Contact us</Link>
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
